import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { getNotificationPreferences, updateNotificationPreference, type PreferenceRow } from "@/lib/notifications.functions";

export const Route = createFileRoute("/_authenticated/notification-settings")({
  head: () => ({ meta: [{ title: "Notification Settings — The Hospitality Reserve" }] }),
  component: NotificationSettingsPage,
});

const TYPE_META: { key: PreferenceRow["type"]; label: string; description: string }[] = [
  { key: "booking_accepted", label: "Booking accepted", description: "When a venue confirms your application or you accept a bartender." },
  { key: "payment_received", label: "Payment received", description: "Confirmation a venue's payment for an engagement has cleared." },
  { key: "shift_reminder_24h", label: "Shift reminder — 24h", description: "Day-before reminder for upcoming engagements." },
  { key: "shift_reminder_2h", label: "Shift reminder — 2h", description: "Last-mile nudge two hours before a shift starts." },
  { key: "shift_starting_soon", label: "Shift starting in 5 min", description: "Final prompt to log arrival just before the shift starts." },
  { key: "shift_completed", label: "Shift completed", description: "When both parties have confirmed an engagement is complete." },
  { key: "payout_sent", label: "Payout sent", description: "When earnings for a completed shift are released." },
  { key: "review_request", label: "Review request", description: "Prompt the venue to review the bartender after a completed shift." },
  { key: "review_received", label: "Review received", description: "When a venue posts a review of your work." },
  { key: "shift_rescheduled", label: "Shift rescheduled", description: "When a venue moves an engagement to a new time." },
  { key: "shift_cancelled", label: "Shift cancelled", description: "When an engagement is cancelled by either party." },
  { key: "no_show_reported", label: "No-show reported", description: "When a venue reports a bartender did not arrive." },
  { key: "arrival_dispute_submitted", label: "Arrival dispute opened", description: "When a bartender challenges a logged arrival time." },
  { key: "arrival_dispute_resolved", label: "Arrival dispute resolved", description: "When an admin upholds or dismisses an arrival dispute." },
  { key: "booking_receipt", label: "Booking receipt", description: "Final receipt with pay breakdown after a shift is completed." },
  { key: "support_ticket_created", label: "Support ticket created", description: "Confirmation with your ticket reference when you raise a support request." },
  { key: "support_ticket_status_changed", label: "Support ticket status changed", description: "When our team moves your ticket to under review or resolved." },
  { key: "support_ticket_note_added", label: "Support ticket note added", description: "When our team adds a note or reply to one of your tickets." },
];

const MANDATORY = new Set<PreferenceRow["type"]>(["booking_accepted", "payment_received", "payout_sent"]);

function NotificationSettingsPage() {
  const fetchPrefs = useServerFn(getNotificationPreferences);
  const updatePref = useServerFn(updateNotificationPreference);
  const [prefs, setPrefs] = useState<PreferenceRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPrefs().then((data) => { setPrefs(data); setLoading(false); }).catch(() => setLoading(false));
  }, [fetchPrefs]);

  const get = (type: PreferenceRow["type"], channel: PreferenceRow["channel"]) =>
    prefs.find((p) => p.type === type && p.channel === channel)?.enabled ?? true;

  const toggle = async (type: PreferenceRow["type"], channel: PreferenceRow["channel"], next: boolean) => {
    const prev = prefs;
    setPrefs((p) => p.map((r) => (r.type === type && r.channel === channel ? { ...r, enabled: next } : r)));
    try {
      await updatePref({ data: { type, channel, enabled: next } });
    } catch (e) {
      setPrefs(prev);
      toast.error(e instanceof Error ? e.message : "Could not update preference.");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader />
      <main className="mx-auto max-w-4xl px-6 py-10">
        <div className="flex items-end justify-between gap-6">
          <div>
            <p className="tracking-wide-luxury text-accent">Account</p>
            <h1 className="mt-3 font-display text-3xl">Notification Preferences</h1>
            <span className="gold-divider mt-4" />
          </div>
          <Link to="/dashboard" className="border border-border px-4 py-2 text-xs tracking-wide-luxury text-muted-foreground hover:text-foreground hover:border-foreground">← Back</Link>
        </div>

        <p className="mt-6 text-sm text-muted-foreground">
          Choose how you'd like to hear from us. Email and SMS channels send when the relevant providers are configured for the workspace.
        </p>

        {loading ? (
          <p className="mt-10 text-sm text-muted-foreground">Loading preferences…</p>
        ) : (
          <div className="mt-8 border border-border bg-card">
            <div className="hidden sm:grid grid-cols-[1fr_80px_80px_80px] px-5 py-3 border-b border-border text-xs tracking-wide-luxury text-muted-foreground uppercase">
              <div>Notification</div>
              <div className="text-center">In-app</div>
              <div className="text-center">Email</div>
              <div className="text-center">SMS</div>
            </div>
            {TYPE_META.map((t) => (
              <div key={t.key} className="grid sm:grid-cols-[1fr_80px_80px_80px] gap-3 px-5 py-4 border-b border-border last:border-b-0 items-center">
                <div>
                  <p className="text-sm font-medium">{t.label}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{t.description}</p>
                </div>
                <div className="flex sm:justify-center items-center gap-2">
                  <span className="sm:hidden text-xs text-muted-foreground">In-app</span>
                  <Switch
                    checked={get(t.key, "in_app")}
                    onCheckedChange={(v) => toggle(t.key, "in_app", v)}
                    disabled={MANDATORY.has(t.key)}
                    aria-label="In-app"
                  />
                </div>
                <div className="flex sm:justify-center items-center gap-2">
                  <span className="sm:hidden text-xs text-muted-foreground">Email</span>
                  <Switch checked={get(t.key, "email")} onCheckedChange={(v) => toggle(t.key, "email", v)} aria-label="Email" />
                </div>
                <div className="flex sm:justify-center items-center gap-2">
                  <span className="sm:hidden text-xs text-muted-foreground">SMS</span>
                  <Switch checked={get(t.key, "sms")} onCheckedChange={(v) => toggle(t.key, "sms", v)} aria-label="SMS" />
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="mt-6 text-xs text-muted-foreground">
          Account-critical in-app alerts (booking accepted, payment received, payout sent) can't be turned off.
        </p>
      </main>
    </div>
  );
}
